import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { ImageProperties } from './ImageProperties';
import type { DocumentSession } from '../core/session';
import { runCommand } from '../semantic/commands/run';
import { AdjustmentPreview } from './AdjustmentPreview';
import { TargetedCurveCommand, type CurveChannel } from '../core/targeted-adjustment';
import {
  Blend,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  FolderPlus,
  Group,
  Image,
  Layers2,
  SquareDashed as Mask,
  Plus,
  Trash2,
  Type,
  Ungroup,
  WandSparkles,
  Shapes,
  X,
} from 'lucide-react';
import {
  AddLayerCommand,
  SetActiveLayersCommand,
  UpdateAdjustmentCommand,
} from '../core/commands';
import {
  compositeUpdates,
  dropLayer,
  groupLayers,
  invertMask,
  removeLayer,
  removeMask,
  renameLayer,
  reorderToGap,
  newRasterLayer,
  runPanelOperation,
  setDropShadow,
  setMaskEnabled,
  setStrokeEffect,
  toggleVisibility,
  ungroupLayer,
} from './layer-intents';
import {
  createAdjustmentLayer,
  createId,
  createGroupLayer,
  findLayer,
  findLayerParent,
  type AdjustmentLayer,
  type BlendMode,
  type EditorDocument,
  type GroupLayer,
  type Layer,
} from '../core/model';
import type { VectorObjectSelection } from './types';
import { layerStyle } from '../core/layer-styles';
import { isSmartObject, SMART_OBJECT_EXTENSION } from '../core/smart-objects';
import { createHighPassSmartFilter, createToneSmartFilter, SetSmartFiltersCommand, smartFilters, type SmartFilter } from '../core/smart-filters';
import { DEFAULT_BLACK_WHITE, type BlackWhiteSettings } from '../filters/tone';
import { layerInIsolation, type IsolationScope } from './isolation';
import { modeLabel, type MediaMode } from '../core/media-mode';
import { actorDisplayName } from './attribution';
import { useExternalChangeHighlight } from './external-change';
import { loadVectorKit } from '../semantic/commands/vector-objects';

const VectorInspector = lazy(() => import('../vector/VectorInspector'));

type LayerDropHint = { id: string; where: 'above' | 'below' | 'into' } | null;

function dropZone(event: React.DragEvent<HTMLElement>, isGroup: boolean): 'above' | 'below' | 'into' {
  const rect = event.currentTarget.getBoundingClientRect();
  const ratio = (event.clientY - rect.top) / Math.max(1, rect.height);
  if (isGroup && ratio >= 0.3 && ratio <= 0.7) return 'into';
  return ratio < 0.5 ? 'above' : 'below';
}

interface LayerPanelProps {
  onCreatePortableText: () => void;
  onDuplicateLayer: () => void;
  onEditImagePixels: (layerId: string) => void;
  session: DocumentSession;
  document: EditorDocument;
  history: import('../core/commands').HistoryManager;
  mediaMode: MediaMode;
  onSwitchMediaMode: (mode: MediaMode) => void;
  maskEditingLayerId: string | null;
  onMaskEditingLayerId: (layerId: string | null) => void;
  vectorObjectSelection: VectorObjectSelection | null;
  onVectorObjectSelection: (selection: VectorObjectSelection | null) => void;
  isolationScope: IsolationScope | null;
  onIsolationScope: (scope: IsolationScope | null) => void;
}

const blendModes: { value: BlendMode; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'multiply', label: 'Multiply' },
  { value: 'screen', label: 'Screen' },
  { value: 'overlay', label: 'Overlay' },
  { value: 'darken', label: 'Darken' },
  { value: 'lighten', label: 'Lighten' },
  { value: 'color-dodge', label: 'Color dodge' },
  { value: 'color-burn', label: 'Color burn' },
  { value: 'hard-light', label: 'Hard light' },
  { value: 'soft-light', label: 'Soft light' },
  { value: 'difference', label: 'Difference' },
  { value: 'exclusion', label: 'Exclusion' },
];

function LayerIcon({ layer }: { layer: Layer }) {
  if (layer.kind === 'group') return <Layers2 size={14} />;
  if (layer.kind === 'adjustment') return <WandSparkles size={14} />;
  if (layer.kind === 'vector') return <Shapes size={14} />;
  if (layer.kind === 'text') return <Type size={14} />;
  return <Image size={14} />;
}

function LayerRows({
  layers,
  depth,
  session,
  document,
  history,
  maskEditingLayerId,
  onMaskEditingLayerId,
  vectorObjectSelection,
  onVectorObjectSelection,
  isolationScope,
  onIsolationScope,
  dropHint,
  onDropHint,
  filter,
  mediaMode,
  highlighted,
}: {
  layers: Layer[];
  depth: number;
  session: DocumentSession;
  document: EditorDocument;
  history: import('../core/commands').HistoryManager;
  maskEditingLayerId: string | null;
  onMaskEditingLayerId: (layerId: string | null) => void;
  vectorObjectSelection: VectorObjectSelection | null;
  onVectorObjectSelection: (selection: VectorObjectSelection | null) => void;
  isolationScope: IsolationScope | null;
  onIsolationScope: (scope: IsolationScope | null) => void;
  dropHint: LayerDropHint;
  onDropHint: (hint: LayerDropHint) => void;
  filter: string;
  mediaMode: MediaMode;
  /** Layers changed by the latest agent or add-on transaction (ADR-009 §6.1), highlighted briefly. */
  highlighted: ReadonlySet<string>;
}) {
  const query = filter.trim().toLowerCase();
  const choose = (event: React.MouseEvent, layer: Layer) => {
    if (!layerInIsolation(document, layer.id, isolationScope)) return;
    const current = document.activeLayerIds;
    const next = event.metaKey || event.ctrlKey
      ? current.includes(layer.id) ? current.filter((id) => id !== layer.id) : [...current, layer.id]
      : [layer.id];
    history.applyTransient(document, new SetActiveLayersCommand(next));
    onVectorObjectSelection(null);
    if (maskEditingLayerId && !next.includes(maskEditingLayerId)) onMaskEditingLayerId(null);
  };

  return layers.map((layer) => {
  const outsideIsolation = !layerInIsolation(document, layer.id, isolationScope);
  const layerText = layer.kind === 'text' ? layer.text.value.replace(/\s+/g, ' ').trim() : '';
  // Changed-by-agent marker (ADR-009 §6.1): the last change to this layer came from an agent transaction; any later user edit clears it.
  const lastActor = session.ledger.provenance(layer.id)?.lastActor;
  const agentChange = lastActor?.kind === 'agent' ? `Changed by ${actorDisplayName(lastActor)}` : null;
  // Groups always render (so nested matches stay reachable); other rows hide
  // when a filter is set and neither the name nor the text content matches.
  const hidden = Boolean(query) && layer.kind !== 'group' && !`${layer.name} ${layerText}`.toLowerCase().includes(query);
  return (
    <div key={layer.id} style={hidden ? { display: 'none' } : undefined}>
      <div
        className={`layer-row ${document.activeLayerIds.includes(layer.id) ? 'selected' : ''} ${outsideIsolation ? 'isolation-outside' : ''} ${dropHint?.id === layer.id ? `drop-${dropHint.where}` : ''} ${highlighted.has(layer.id) ? 'external-change' : ''}`}
        data-layer-id={layer.id}
        role="treeitem"
        aria-selected={document.activeLayerIds.includes(layer.id)}
        aria-expanded={layer.kind === 'group' ? layer.expanded : undefined}
        tabIndex={document.activeLayerIds.includes(layer.id) ? 0 : -1}
        style={{ paddingLeft: 8 + depth * 16 }}
        draggable={!outsideIsolation}
        onDragStart={(event) => {
          event.dataTransfer.setData('application/x-maipo-layer', layer.id);
          event.dataTransfer.effectAllowed = 'move';
        }}
        onDragOver={(event) => {
          if (![...event.dataTransfer.types].includes('application/x-maipo-layer')) return;
          event.preventDefault();
          event.stopPropagation();
          onDropHint({ id: layer.id, where: dropZone(event, layer.kind === 'group') });
        }}
        onDragLeave={() => { if (dropHint?.id === layer.id) onDropHint(null); }}
        onDrop={(event) => {
          const draggedId = event.dataTransfer.getData('application/x-maipo-layer');
          onDropHint(null);
          if (!draggedId || draggedId === layer.id) return;
          event.preventDefault();
          event.stopPropagation();
          runPanelOperation(session, dropLayer(document, draggedId, layer, dropZone(event, layer.kind === 'group')));
        }}
        onClick={(event) => choose(event, layer)}
        onDoubleClick={() => {
          if (outsideIsolation) return;
          if (layer.kind === 'group') {
            if (layer.extensions[SMART_OBJECT_EXTENSION]) runCommand(session, 'layer.setExpanded', { layer: layer.id, expanded: true });
            else {
              onIsolationScope({ layerId: layer.id });
              history.applyTransient(document, new SetActiveLayersCommand([layer.id]));
              onVectorObjectSelection(null);
            }
            return;
          }
          const name = window.prompt('Layer name', layer.name)?.trim();
          if (name && name !== layer.name) runPanelOperation(session, renameLayer(layer.id, name));
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight' && layer.kind === 'group' && !layer.expanded) {
            event.preventDefault(); runCommand(session, 'layer.setExpanded', { layer: layer.id, expanded: true }); return;
          }
          if (event.key === 'ArrowLeft' && layer.kind === 'group' && layer.expanded) {
            event.preventDefault(); runCommand(session, 'layer.setExpanded', { layer: layer.id, expanded: false }); return;
          }
          if (event.key === 'ArrowLeft') {
            const parent = findLayerParent(document.root, layer.id)?.parent;
            if (parent && parent !== document.root) { event.preventDefault(); history.applyTransient(document, new SetActiveLayersCommand([parent.id])); }
            return;
          }
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Home' || event.key === 'End') {
            const rows = [...event.currentTarget.closest('[role="tree"]')?.querySelectorAll<HTMLElement>('[role="treeitem"]') ?? []];
            const index = rows.indexOf(event.currentTarget);
            const target = event.key === 'Home' ? rows[0] : event.key === 'End' ? rows.at(-1) : rows[index + (event.key === 'ArrowUp' ? -1 : 1)];
            if (target) { event.preventDefault(); target.focus(); target.click(); }
          }
        }}
      >
        <button
          type="button"
          className="layer-visibility"
          disabled={outsideIsolation}
          aria-label={`${layer.visible ? 'Hide' : 'Show'} ${layer.name}`}
          onClick={(event) => {
            event.stopPropagation();
            runPanelOperation(session, toggleVisibility(layer));
          }}
        >
          {layer.visible ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        <span className={`layer-thumb layer-${layer.kind}`}><LayerIcon layer={layer} /></span>
        <div className="layer-label">
          <span className="layer-name">{layer.name}</span>
          {layerText ? <span className="layer-subtitle" title={layerText}>{layerText}</span> : null}
        </div>
        {layer.blendMode !== 'normal' ? <span className="layer-blend-badge" title={`Blend: ${layer.blendMode}`}>{layer.blendMode.slice(0, 3)}</span> : null}
        {layer.opacity < 1 ? <span className="layer-op-badge" title="Opacity">{Math.round(layer.opacity * 100)}</span> : null}
        {layer.clipping ? <span className="layer-badge" title="Clipped">↳</span> : null}
        {agentChange ? <span className="layer-agent-marker" role="img" aria-label={agentChange} title={agentChange} /> : null}
        {isSmartObject(layer) ? <span className="layer-badge smart-object-badge" title="Embedded Smart Object · double-click to edit contents">◆ smart{smartFilters(layer).length ? ` · ${smartFilters(layer).length} fx` : ''}</span> : null}
        {layer.mask && mediaMode !== 'vector' ? <button type="button" disabled={layer.kind !== 'raster'} className={`layer-mask-badge ${maskEditingLayerId === layer.id ? 'active' : ''}`} title={layer.kind !== 'raster' ? 'Brush editing is currently available only for raster-layer masks.' : maskEditingLayerId === layer.id ? 'Editing layer mask' : 'Edit layer mask'} onClick={(event) => {
          event.stopPropagation();
          if (layer.kind !== 'raster') return;
          history.applyTransient(document, new SetActiveLayersCommand([layer.id]));
          onMaskEditingLayerId(maskEditingLayerId === layer.id ? null : layer.id);
        }}><Mask size={12} /></button> : null}
        {layer.kind === 'group' ? <button type="button" className="group-expansion" aria-label={`${layer.expanded ? 'Collapse' : 'Expand'} ${layer.name}`} onClick={(event) => { event.stopPropagation(); runCommand(session, 'layer.setExpanded', { layer: layer.id, expanded: !layer.expanded }); }}>{layer.expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</button> : null}
      </div>
      {layer.kind === 'vector' && mediaMode !== 'raster' ? layer.vector.objects.map((object) => {
        const selected = vectorObjectSelection?.layerId === layer.id && vectorObjectSelection.objectIds.includes(object.id);
        const objectOutsideIsolation = Boolean(isolationScope?.objectId && (isolationScope.layerId !== layer.id || isolationScope.objectId !== object.id));
        return <div
          key={object.id}
          className={`layer-row vector-object-row ${selected ? 'selected' : ''} ${objectOutsideIsolation ? 'isolation-outside' : ''}`}
          style={{ paddingLeft: 24 + depth * 16 }}
          role="treeitem"
          aria-selected={selected}
          onClick={(event) => {
            event.stopPropagation();
            if (objectOutsideIsolation) return;
            if (!document.activeLayerIds.includes(layer.id)) history.applyTransient(document, new SetActiveLayersCommand([layer.id]));
            const current = vectorObjectSelection?.layerId === layer.id ? vectorObjectSelection.objectIds : [];
            const objectIds = event.metaKey || event.ctrlKey || event.shiftKey
              ? current.includes(object.id) ? current.filter((id) => id !== object.id) : [...current, object.id]
              : [object.id];
            onVectorObjectSelection(objectIds.length ? { layerId: layer.id, objectIds } : null);
          }}
          onDoubleClick={(event) => {
            event.stopPropagation();
            if (objectOutsideIsolation) return;
            history.applyTransient(document, new SetActiveLayersCommand([layer.id]));
            onVectorObjectSelection({ layerId: layer.id, objectIds: [object.id], scope: 'object' });
            onIsolationScope({ layerId: layer.id, objectId: object.id });
          }}
        >
          <span className="object-tree-rail" />
          <span className="layer-thumb layer-vector-object"><Shapes size={12} /></span>
          <span className="layer-name">{object.name}</span>
          <span className="layer-badge">{object.type}</span>
        </div>;
      }) : null}
      {layer.kind === 'group' && layer.expanded ? (
        <LayerRows layers={layer.children} depth={depth + 1} session={session} document={document} history={history} mediaMode={mediaMode} maskEditingLayerId={maskEditingLayerId} onMaskEditingLayerId={onMaskEditingLayerId} vectorObjectSelection={vectorObjectSelection} onVectorObjectSelection={onVectorObjectSelection} isolationScope={isolationScope} onIsolationScope={onIsolationScope} dropHint={dropHint} onDropHint={onDropHint} filter={filter} highlighted={highlighted} />
      ) : null}
    </div>
  );
  });
}

function AdjustmentProperties({ layer, document, history }: { layer: AdjustmentLayer; document: EditorDocument; history: import('../core/commands').HistoryManager }) {
  const [targetedCurves, setTargetedCurves] = useState(false);
  const [targetedChannel, setTargetedChannel] = useState<CurveChannel>('rgb');
  const change = (patch: Partial<AdjustmentLayer['adjustment']>) => history.execute(document, new UpdateAdjustmentCommand(layer.id, patch));
  const curveMidpoint = (channel: keyof NonNullable<AdjustmentLayer['adjustment']['curves']>) => {
    const point = layer.adjustment.curves?.[channel].find((entry) => entry.x === 0.5);
    return point ? point.y * 2 - 1 : 0;
  };
  const changeCurve = (channel: keyof NonNullable<AdjustmentLayer['adjustment']['curves']>, value: number) => change({
    curves: {
      rgb: [...(layer.adjustment.curves?.rgb ?? [])],
      red: [...(layer.adjustment.curves?.red ?? [])],
      green: [...(layer.adjustment.curves?.green ?? [])],
      blue: [...(layer.adjustment.curves?.blue ?? [])],
      [channel]: value === 0 ? [] : [{ x: 0.5, y: (value + 1) / 2 }],
    },
  });
  const gradient = layer.adjustment.gradientMap ?? {
    enabled: false,
    reverse: false,
    stops: [{ position: 0, color: '#000000' }, { position: 0.5, color: '#808080' }, { position: 1, color: '#ffffff' }],
  };
  const levels = layer.adjustment.levels ?? { inputBlack: 0, inputWhite: 1, gamma: 1, outputBlack: 0, outputWhite: 1 };
  const blackWhite = layer.adjustment.blackWhite ?? { ...DEFAULT_BLACK_WHITE };
  const changeBlackWhite = (patch: Partial<BlackWhiteSettings>) => change({ blackWhite: { ...blackWhite, ...patch } });
  const changeLevel = (key: keyof typeof levels, value: number) => {
    const next = { ...levels, [key]: value };
    if (next.inputBlack >= next.inputWhite) {
      if (key === 'inputBlack') next.inputBlack = Math.max(0, next.inputWhite - 1 / 255);
      else next.inputWhite = Math.min(1, next.inputBlack + 1 / 255);
    }
    if (next.outputBlack > next.outputWhite) {
      if (key === 'outputBlack') next.outputBlack = next.outputWhite;
      else next.outputWhite = next.outputBlack;
    }
    change({ levels: next });
  };
  const changeGradientStop = (index: number, color: string) => change({
    gradientMap: { ...gradient, stops: gradient.stops.map((stop, stopIndex) => stopIndex === index ? { ...stop, color } : stop) },
  });
  return (
    <div className="adjustment-properties">
      <div className="smart-filter-actions"><button type="button" aria-pressed={targetedCurves} onClick={() => setTargetedCurves((value) => !value)}>☝ Targeted Curves</button><select aria-label="Targeted Curves channel" value={targetedChannel} onChange={(event) => setTargetedChannel(event.target.value as CurveChannel)}><option value="rgb">RGB</option><option value="red">Red</option><option value="green">Green</option><option value="blue">Blue</option></select></div>
      <AdjustmentPreview document={document} layer={layer} targeted={targetedCurves} onTargetStart={(input) => {
        history.begin(document, new TargetedCurveCommand(layer.id, targetedChannel, input));
      }} onTargetDelta={(delta) => {
        history.updateLive(document, (command) => {
          if (command instanceof TargetedCurveCommand) command.update(document, delta);
        });
      }} onTargetEnd={(cancelled) => {
        if (cancelled) history.cancelLive(document);
        else history.commitLive(document);
      }} />
      <label>Brightness <output>{Math.round(layer.adjustment.brightness * 100)}</output>
        <input type="range" min="-1" max="1" step="0.01" value={layer.adjustment.brightness} onChange={(event) => change({ brightness: Number(event.target.value) })} />
      </label>
      <label>Contrast <output>{Math.round(layer.adjustment.contrast * 100)}</output>
        <input type="range" min="-0.9" max="0.9" step="0.01" value={layer.adjustment.contrast} onChange={(event) => change({ contrast: Number(event.target.value) })} />
      </label>
      <label>Saturation <output>{Math.round(layer.adjustment.saturation * 100)}</output>
        <input type="range" min="-1" max="1" step="0.01" value={layer.adjustment.saturation} onChange={(event) => change({ saturation: Number(event.target.value) })} />
      </label>
      <label className="check-row"><input type="checkbox" checked={layer.adjustment.invert} onChange={(event) => change({ invert: event.target.checked })} /> Invert colors</label>
      <fieldset className="adjustment-section black-white-adjustment">
        <legend>Black &amp; White</legend>
        <label className="check-row"><input type="checkbox" checked={blackWhite.enabled} onChange={(event) => changeBlackWhite({ enabled: event.target.checked })} /> Enable color mixer</label>
        {([
          ['red', 'Reds'],
          ['yellow', 'Yellows'],
          ['green', 'Greens'],
          ['cyan', 'Cyans'],
          ['blue', 'Blues'],
          ['magenta', 'Magentas'],
        ] as const).map(([key, label]) => <label key={key}>{label} <output>{blackWhite[key]}%</output>
          <input type="range" min="-200" max="300" step="1" disabled={!blackWhite.enabled} value={blackWhite[key]} onChange={(event) => changeBlackWhite({ [key]: Number(event.target.value) })} />
        </label>)}
        <label className="check-row"><input type="checkbox" disabled={!blackWhite.enabled} checked={blackWhite.tint} onChange={(event) => changeBlackWhite({ tint: event.target.checked })} /> Tint</label>
        <label>Tint color<input type="color" disabled={!blackWhite.enabled || !blackWhite.tint} value={blackWhite.tintColor} onChange={(event) => changeBlackWhite({ tintColor: event.target.value })} /></label>
      </fieldset>
      <fieldset className="adjustment-section">
        <legend>Levels</legend>
        {([
          ['inputBlack', 'Input black', 0, 1, 0.01],
          ['inputWhite', 'Input white', 0, 1, 0.01],
          ['gamma', 'Gamma', 0.1, 3, 0.01],
          ['outputBlack', 'Output black', 0, 1, 0.01],
          ['outputWhite', 'Output white', 0, 1, 0.01],
        ] as const).map(([key, label, min, max, step]) => (
          <label key={key}>{label} <output>{key === 'gamma' ? levels[key].toFixed(2) : Math.round(levels[key] * 255)}</output>
            <input type="range" min={min} max={max} step={step} value={levels[key]} onChange={(event) => changeLevel(key, Number(event.target.value))} />
          </label>
        ))}
      </fieldset>
      <fieldset className="adjustment-section">
        <legend>Curves</legend>
        {([
          ['rgb', 'RGB'],
          ['red', 'Red'],
          ['green', 'Green'],
          ['blue', 'Blue'],
        ] as const).map(([channel, label]) => (
          <label key={channel}>{label} midtone <output>{Math.round(curveMidpoint(channel) * 100)}</output>
            <input type="range" min="-1" max="1" step="0.01" value={curveMidpoint(channel)} onChange={(event) => changeCurve(channel, Number(event.target.value))} />
          </label>
        ))}
      </fieldset>
      <fieldset className="adjustment-section">
        <legend>Gradient map</legend>
        <label className="check-row"><input type="checkbox" checked={gradient.enabled} onChange={(event) => change({ gradientMap: { ...gradient, enabled: event.target.checked } })} /> Enable gradient map</label>
        <div className="gradient-map-stops">
          {gradient.stops.map((stop, index) => <label key={stop.position}>{index === 0 ? 'Shadows' : index === gradient.stops.length - 1 ? 'Highlights' : 'Midtones'}<input type="color" value={stop.color} onChange={(event) => changeGradientStop(index, event.target.value)} /></label>)}
        </div>
        <label className="check-row"><input type="checkbox" checked={gradient.reverse} onChange={(event) => change({ gradientMap: { ...gradient, reverse: event.target.checked } })} /> Reverse</label>
      </fieldset>
    </div>
  );
}

function SmartFilterProperties({ layer, document, history }: { layer: GroupLayer; document: EditorDocument; history: import('../core/commands').HistoryManager }) {
  const filters = smartFilters(layer);
  const set = (next: readonly SmartFilter[], label: string) => history.execute(document, new SetSmartFiltersCommand(layer.id, next, label));
  const patch = (id: string, update: (filter: SmartFilter) => SmartFilter, label: string) => set(filters.map((filter) => filter.id === id ? update(filter) : filter), label);
  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (!filters[index] || !filters[target]) return;
    const next = [...filters];
    [next[index], next[target]] = [next[target]!, next[index]!];
    set(next, 'Reorder Smart Filters');
  };
  return <section className="smart-filter-properties"><div className="palette-heading"><span>Smart Filters</span><span>{filters.length} filter{filters.length === 1 ? '' : 's'}</span><div><button type="button" onClick={() => set([...filters, createToneSmartFilter()], 'Add Smart Filter')}>+ Tone</button><button type="button" onClick={() => set([...filters, createHighPassSmartFilter()], 'Add High Pass Smart Filter')}>+ High Pass</button></div></div>
    {filters.map((filter, index) => <div className="smart-filter-row" key={filter.id}>
      <div><label><input type="checkbox" checked={filter.enabled} onChange={(event) => patch(filter.id, (current) => ({ ...current, enabled: event.target.checked }), 'Toggle Smart Filter')} /> {filter.name}</label><span>Non-destructive {filter.type === 'tone' ? 'tone' : filter.type === 'high-pass' ? 'edge' : 'map'} filter</span></div>
      {filter.type === 'tone' ? <div className="effect-grid">
        <label>Brightness<input type="number" min="-100" max="100" value={Math.round(filter.settings.brightness * 100)} onChange={(event) => patch(filter.id, (current) => current.type === 'tone' ? { ...current, settings: { ...current.settings, brightness: Number(event.target.value) / 100 } } : current, 'Edit Smart Filter')} /></label>
        <label>Contrast<input type="number" min="-99" max="99" value={Math.round(filter.settings.contrast * 100)} onChange={(event) => patch(filter.id, (current) => current.type === 'tone' ? { ...current, settings: { ...current.settings, contrast: Number(event.target.value) / 100 } } : current, 'Edit Smart Filter')} /></label>
        <label>Saturation<input type="number" min="-100" max="100" value={Math.round(filter.settings.saturation * 100)} onChange={(event) => patch(filter.id, (current) => current.type === 'tone' ? { ...current, settings: { ...current.settings, saturation: Number(event.target.value) / 100 } } : current, 'Edit Smart Filter')} /></label>
        <label><input type="checkbox" checked={filter.settings.invert} onChange={(event) => patch(filter.id, (current) => current.type === 'tone' ? { ...current, settings: { ...current.settings, invert: event.target.checked } } : current, 'Edit Smart Filter')} /> Invert</label>
      </div> : filter.type === 'high-pass' ? <div className="effect-grid"><label>Radius<input type="number" min="1" max="64" value={filter.radius} onChange={(event) => patch(filter.id, (current) => current.type === 'high-pass' ? { ...current, radius: Number(event.target.value) } : current, 'Edit High Pass Smart Filter')} /></label><span>px · neutral gray edge detail</span></div> : <div className="effect-grid">
        <label>Horizontal<input type="number" min="-1000" max="1000" value={filter.settings.horizontalScale} onChange={(event) => patch(filter.id, (current) => current.type === 'displace' ? { ...current, settings: { ...current.settings, horizontalScale: Number(event.target.value) } } : current, 'Edit Displace Smart Filter')} /></label>
        <label>Vertical<input type="number" min="-1000" max="1000" value={filter.settings.verticalScale} onChange={(event) => patch(filter.id, (current) => current.type === 'displace' ? { ...current, settings: { ...current.settings, verticalScale: Number(event.target.value) } } : current, 'Edit Displace Smart Filter')} /></label>
        <label>Map mode<select value={filter.settings.mode} onChange={(event) => patch(filter.id, (current) => current.type === 'displace' ? { ...current, settings: { ...current.settings, mode: event.target.value === 'tile' ? 'tile' : 'fit' } } : current, 'Edit Displace Smart Filter')}><option value="fit">Fit</option><option value="tile">Tile</option></select></label>
        <span>{filter.map.width} × {filter.map.height} map</span>
      </div>}
      <div><button type="button" disabled={index === 0} onClick={() => move(index, -1)}>↑</button><button type="button" disabled={index === filters.length - 1} onClick={() => move(index, 1)}>↓</button><button type="button" onClick={() => {
        const duplicate = filter.type === 'tone' ? createToneSmartFilter(`${filter.name} copy`) : filter.type === 'high-pass' ? createHighPassSmartFilter(filter.radius) : structuredClone(filter);
        if (duplicate.type === 'tone' && filter.type === 'tone') duplicate.settings = structuredClone(filter.settings);
        else if (duplicate.type === 'displace') {
          duplicate.id = createId('smart-filter');
          duplicate.name = `${filter.name} copy`;
        }
        else duplicate.name = `${filter.name} copy`;
        set([...filters.slice(0, index + 1), duplicate, ...filters.slice(index + 1)], 'Duplicate Smart Filter');
      }}>Duplicate</button><button type="button" className="quiet-danger" onClick={() => set(filters.filter((candidate) => candidate.id !== filter.id), 'Delete Smart Filter')}>Delete</button></div>
    </div>)}
  </section>;
}

function LayerStyleProperties({ layer, session }: { layer: Layer; session: DocumentSession }) {
  const style = layerStyle(layer);
  const shadow = style.dropShadow ?? { enabled: false, color: '#000000', opacity: 0.55, blur: 12, offsetX: 5, offsetY: 5 };
  const stroke = style.stroke ?? { enabled: false, color: '#ffffff', opacity: 1, width: 2 };
  const changeShadow = (next: typeof shadow) => runPanelOperation(session, setDropShadow(layer.id, next));
  const changeStroke = (next: typeof stroke) => runPanelOperation(session, setStrokeEffect(layer.id, next));
  return <details className="layer-style-properties">
    <summary>Layer styles <span>{[shadow.enabled, stroke.enabled].filter(Boolean).length || ''}</span></summary>
    <fieldset>
      <legend><label><input type="checkbox" checked={shadow.enabled} onChange={(event) => changeShadow({ ...shadow, enabled: event.target.checked })} /> Drop shadow</label></legend>
      <label>Color<input type="color" value={shadow.color} onChange={(event) => changeShadow({ ...shadow, color: event.target.value })} /></label>
      <label>Opacity<input type="range" min="0" max="1" step="0.01" value={shadow.opacity} onChange={(event) => changeShadow({ ...shadow, opacity: Number(event.target.value) })} /></label>
      <label>Blur<input type="range" min="0" max="64" step="1" value={shadow.blur} onChange={(event) => changeShadow({ ...shadow, blur: Number(event.target.value) })} /></label>
      <label>X offset<input type="number" min="-128" max="128" value={shadow.offsetX} onChange={(event) => changeShadow({ ...shadow, offsetX: Number(event.target.value) })} /></label>
      <label>Y offset<input type="number" min="-128" max="128" value={shadow.offsetY} onChange={(event) => changeShadow({ ...shadow, offsetY: Number(event.target.value) })} /></label>
    </fieldset>
    <fieldset>
      <legend><label><input type="checkbox" checked={stroke.enabled} onChange={(event) => changeStroke({ ...stroke, enabled: event.target.checked })} /> Outside stroke</label></legend>
      <label>Color<input type="color" value={stroke.color} onChange={(event) => changeStroke({ ...stroke, color: event.target.value })} /></label>
      <label>Width<input type="range" min="1" max="32" step="1" value={stroke.width} onChange={(event) => changeStroke({ ...stroke, width: Number(event.target.value) })} /></label>
    </fieldset>
  </details>;
}

export function LayerPanel({ onCreatePortableText, onDuplicateLayer, onEditImagePixels, session, document, history, mediaMode, onSwitchMediaMode, maskEditingLayerId, onMaskEditingLayerId, vectorObjectSelection, onVectorObjectSelection, isolationScope, onIsolationScope }: LayerPanelProps) {
  const [dropHint, setDropHint] = useState<LayerDropHint>(null);
  const [filter, setFilter] = useState('');
  // ADR-009 §6.1: briefly highlight rows an agent or add-on just changed, and
  // bring a layer it created into view, without moving focus or selection.
  const externalChange = useExternalChangeHighlight(session);
  const highlighted = new Set(externalChange?.layers ?? []);
  const layerListRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const created = externalChange?.created.find((id) => externalChange.layers.includes(id));
    if (!created) return;
    const row = [...layerListRef.current?.querySelectorAll<HTMLElement>('.layer-row[data-layer-id]') ?? []].find((candidate) => candidate.dataset.layerId === created);
    if (!row || typeof row.scrollIntoView !== 'function') return;
    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    row.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
  }, [externalChange]);
  const externalChangeNames = externalChange ? externalChange.layers.map((id) => findLayer(document.root, id)?.name).filter((name): name is string => Boolean(name)) : [];
  const active = document.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
  const activeMedium = active?.kind === 'vector' || active?.kind === 'text'
    ? 'vector'
    : active?.kind === 'raster' || active?.kind === 'image' || active?.kind === 'adjustment'
      ? 'raster'
      : null;
  const activeHiddenByWorkspace = activeMedium !== null && mediaMode !== 'all' && activeMedium !== mediaMode;
  const showRasterProperties = mediaMode === 'all' || (mediaMode === 'raster' && activeMedium !== 'vector');
  const selectedCount = document.activeLayerIds.length;
  // Composite edits apply to every selected layer, so you can set blend/opacity
  // on a whole group of layers at once.
  const applyComposite = (patch: { blendMode?: BlendMode; opacity?: number }, label: string) => {
    for (const operation of compositeUpdates(document.activeLayerIds, patch, label)) runPanelOperation(session, operation);
  };
  const selectedObjectId = active?.kind === 'vector' && vectorObjectSelection?.layerId === active.id ? vectorObjectSelection.objectIds[0] : undefined;
  const addRaster = () => runPanelOperation(session, newRasterLayer(document));
  const addGroup = () => history.execute(document, new AddLayerCommand(createGroupLayer('New group')));
  const addAdjustment = () => history.execute(document, new AddLayerCommand(createAdjustmentLayer()));
  const addShape = () => void import('../vector/index').then((module) => {
    module.activateVectorSubsystem();
    const layer = module.createShapeLayer('rectangle');
    history.execute(document, new AddLayerCommand(layer));
    const object = layer.vector.objects[0];
    onVectorObjectSelection(object ? { layerId: layer.id, objectIds: [object.id] } : null);
  });
  const addText = () => void import('../vector/index').then((module) => {
    module.activateVectorSubsystem();
    const layer = module.createTextLayer();
    // Keep a newly created annotation visible even in a small imported image.
    const x = Math.min(160, document.width * 0.1);
    const y = Math.min(180, document.height * 0.1);
    layer.transform = [1, 0, 0, 1, x, y];
    // Project validation needs at least 1px for both; a tiny canvas must not
    // produce text that makes every save fail.
    layer.text.box = { ...layer.text.box, width: Math.max(1, Math.min(layer.text.box.width, document.width - 2 * x)) };
    layer.text.style = { ...layer.text.style, size: Math.max(1, Math.min(layer.text.style.size, document.height * 0.2, document.width / 8)) };
    history.execute(document, new AddLayerCommand(layer));
    onVectorObjectSelection(null);
  });
  const move = (direction: -1 | 1) => {
    if (!active) return;
    const location = findLayerParent(document.root, active.id);
    if (!location) return;
    // The command takes an insertion-gap index, and hidden siblings (e.g.
    // PSD previews) are skipped so every step is a visible reorder.
    const siblings = location.parent.children;
    let step = location.index + direction;
    while (siblings[step] !== undefined && !siblings[step]?.visible) step += direction;
    const gap = direction === -1 ? Math.max(0, step) : Math.min(siblings.length, step + 1);
    runPanelOperation(session, reorderToGap(document, active.id, location.parent === document.root ? 'root' : location.parent.id, gap));
  };
  const removeSelection = () => {
    if (active?.kind === 'vector' && selectedObjectId) {
      // The vector object command (the agents' vector.deleteObjects) compiles to
      // the same RemoveVectorObjectCommand; its runtime still loads lazily.
      void loadVectorKit().then(() => {
        runCommand(session, 'vector.deleteObjects', { layer: active.id, objects: [selectedObjectId] });
        onVectorObjectSelection(null);
      });
      return;
    }
    if (active) runPanelOperation(session, removeLayer(active.id));
  };

  return (
    <div className={`layers-panel${active?.kind === 'text' && active.text.schemaVersion === 2 ? ' portable-text-panel' : ''}`}>
      <div className="layer-composite-controls">
        {selectedCount > 1 ? <span className="composite-scope">Editing {selectedCount} layers</span> : null}
        <select
          aria-label="Layer blend mode"
          disabled={!active}
          value={active?.blendMode ?? 'normal'}
          onChange={(event) => active && applyComposite({ blendMode: event.target.value as BlendMode }, 'Change blend mode')}
        >
          {blendModes.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
        </select>
        <label>Opacity
          <input
            type="number"
            min="0"
            max="100"
            value={Math.round((active?.opacity ?? 1) * 100)}
            disabled={!active}
            onChange={(event) => active && applyComposite({ opacity: Math.max(0, Math.min(1, Number(event.target.value) / 100)) }, 'Change opacity')}
          />
          <span>%</span>
        </label>
      </div>
      {activeHiddenByWorkspace ? <div className="media-context-notice" role="status">
        <p><strong>{active?.name}</strong> remains in the document, but its {activeMedium} editing controls are hidden by the {modeLabel(mediaMode)} workspace.</p>
        <button type="button" onClick={() => onSwitchMediaMode(activeMedium)}>Switch to {modeLabel(activeMedium)}</button>
      </div> : null}
      {active?.kind === 'image' && showRasterProperties ? <ImageProperties key={`${session.sessionId}:${active.id}`} layer={active} session={session} onEditPixels={() => onEditImagePixels(active.id)} /> : null}
      {active?.mask && showRasterProperties ? <div className="mask-properties">
        <div><strong>Layer mask</strong><span>{active.kind !== 'raster' ? 'Display, export, invert, and hit-testing are supported; brush editing is raster-only.' : maskEditingLayerId === active.id ? 'Brushes edit mask luminance' : 'Select the mask to refine it'}</span></div>
        <button type="button" disabled={active.kind !== 'raster'} title={active.kind !== 'raster' ? 'Brush editing is currently available only for raster-layer masks.' : undefined} className={maskEditingLayerId === active.id ? 'active' : ''} onClick={() => onMaskEditingLayerId(maskEditingLayerId === active.id ? null : active.id)}>{maskEditingLayerId === active.id ? 'Editing mask' : 'Edit mask'}</button>
        <label><input type="checkbox" checked={active.mask.enabled} onChange={(event) => runPanelOperation(session, setMaskEnabled(active.id, event.target.checked))} /> Enabled</label>
        <label title="Independent mask transforms are not available yet."><input type="checkbox" checked disabled /> Linked</label>
        <button type="button" onClick={() => runPanelOperation(session, invertMask(active.id))}>Invert</button>
        <button type="button" className="quiet-danger" onClick={() => { runPanelOperation(session, removeMask(active.id)); onMaskEditingLayerId(null); }}>Remove</button>
      </div> : null}
      {active?.kind === 'adjustment' && showRasterProperties ? <AdjustmentProperties layer={active} document={document} history={history} /> : null}
      {isSmartObject(active) && showRasterProperties ? <SmartFilterProperties layer={active} document={document} history={history} /> : null}
      {active && showRasterProperties ? <LayerStyleProperties layer={active} session={session} /> : null}
      {(active?.kind === 'vector' || active?.kind === 'text') && mediaMode !== 'raster' ? <Suspense fallback={<div className="vector-loading">Loading vector controls…</div>}><VectorInspector session={session} layer={active} document={document} history={history} selectedObjectIds={vectorObjectSelection?.layerId === active.id ? vectorObjectSelection.objectIds : []} selectedNodeIds={vectorObjectSelection?.layerId === active.id && vectorObjectSelection.scope === 'node' ? vectorObjectSelection.nodeIds ?? [] : []} /></Suspense> : null}
      <div className="layer-filter-row">
        <input className="layer-filter" type="text" placeholder="Filter layers…" aria-label="Filter layers" value={filter} onChange={(event) => setFilter(event.target.value)} />
        {filter ? <button type="button" className="layer-filter-clear" aria-label="Clear filter" onClick={() => setFilter('')}><X size={13} /></button> : null}
      </div>
      <div className="layer-list" role="tree" aria-label="Layers" ref={layerListRef}>
        <LayerRows layers={document.root.children} depth={0} session={session} document={document} history={history} mediaMode={mediaMode} maskEditingLayerId={maskEditingLayerId} onMaskEditingLayerId={onMaskEditingLayerId} vectorObjectSelection={vectorObjectSelection} onVectorObjectSelection={onVectorObjectSelection} isolationScope={isolationScope} onIsolationScope={onIsolationScope} dropHint={dropHint} onDropHint={setDropHint} filter={filter} highlighted={highlighted} />
      </div>
      <div className="sr-only layer-change-status" aria-live="polite" aria-atomic="true">
        {externalChange && externalChangeNames.length ? `${actorDisplayName(externalChange.actor)} changed ${externalChangeNames.slice(0, 3).join(', ')}${externalChangeNames.length > 3 ? ` and ${externalChangeNames.length - 3} more` : ''}.` : ''}
      </div>
      <div className="layer-actions" aria-label="Layer actions">
        <div className="layer-actions-group" role="group" aria-label="Add layer">
          {mediaMode !== 'vector' ? <button type="button" onClick={addRaster} title="New raster layer" aria-label="New raster layer"><Plus size={15} /></button> : null}
          {mediaMode !== 'raster' ? <button type="button" onClick={addText} title="New editable text" aria-label="New editable text"><Type size={15} /></button> : null}
          {mediaMode !== 'raster' ? <button type="button" className="portable-text-add" onClick={onCreatePortableText} title="New portable text" aria-label="New portable text"><Type size={15} /><span>Portable</span></button> : null}
          {mediaMode !== 'raster' ? <button type="button" onClick={addShape} title="New editable shape" aria-label="New editable shape"><Shapes size={15} /></button> : null}
          <button type="button" onClick={addGroup} title="New group" aria-label="New group"><FolderPlus size={15} /></button>
          {mediaMode !== 'vector' ? <button type="button" onClick={addAdjustment} title="New tone adjustment" aria-label="New tone adjustment"><Blend size={15} /></button> : null}
        </div>
        <div className="layer-actions-group" role="group" aria-label="Arrange layer">
          <button type="button" disabled={!active} onClick={() => move(-1)} title="Move up" aria-label="Move layer up"><ChevronUp size={15} /></button>
          <button type="button" disabled={!active} onClick={() => move(1)} title="Move down" aria-label="Move layer down"><ChevronDown size={15} /></button>
          <button type="button" disabled={!active} onClick={onDuplicateLayer} title="Duplicate layer" aria-label="Duplicate layer"><Copy size={15} /></button>
          <button type="button" disabled={document.activeLayerIds.length === 0} onClick={() => runPanelOperation(session, groupLayers(document, document.activeLayerIds))} title="Group selected" aria-label="Group selected layers"><Group size={15} /></button>
          <button type="button" disabled={active?.kind !== 'group'} onClick={() => active && runPanelOperation(session, ungroupLayer(active.id))} title="Ungroup" aria-label="Ungroup layer"><Ungroup size={15} /></button>
        </div>
        <div className="layer-actions-group" role="group" aria-label="Mask and delete">
          {mediaMode !== 'vector' ? <button type="button" disabled={active?.kind !== 'raster'} aria-label="Create mask from selection" onClick={() => {
            if (!active || active.kind !== 'raster') return;
            runCommand(session, 'mask.fromSelection', { layer: active.id });
            onMaskEditingLayerId(active.id);
          }} title="Mask from selection"><Mask size={15} /></button> : null}
          <button type="button" className="layer-action-danger" disabled={!active} onClick={removeSelection} title={selectedObjectId ? 'Delete selected object' : 'Delete layer'} aria-label={selectedObjectId ? 'Delete selected object' : 'Delete layer'}><Trash2 size={15} /></button>
        </div>
      </div>
    </div>
  );
}
